import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Close } from '../../../assets/images';
import { useUserStore } from '../../../utils/userStore';
import './contact-us.scss';

interface ContactUsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactUsDrawer({ isOpen, onClose }: ContactUsDrawerProps) {
  const { t } = useTranslation();
  const userInfo = useUserStore((state) => state.userInfo);
  const [result, setResult] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenUrl, setScreenUrl] = useState("");
  const [errors, setErrors] = useState<{type?: boolean, message?: boolean}>({});

  useEffect(() => {
    if (isOpen) {
      setScreenUrl(window.location.pathname);
      setResult("");
    }
  }, [isOpen]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const typeValue = formData.get("type");
    const messageValue = formData.get("message");

    const newErrors: { type?: boolean, message?: boolean } = {};
    if (!typeValue) newErrors.type = true;
    if (!messageValue) newErrors.message = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    setIsSubmitting(true);
    setResult("");
    
    formData.append("access_key", import.meta.env.VITE_WEB3FORMS_ACCESS_KEY || "");
    formData.append("subject", t('contactUsSubject'));

    if (userInfo?.email) {
      formData.append("email", userInfo.email);
    }
    const fullName = `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim();
    if (fullName) {
      formData.append("name", fullName);
    }
    if (userInfo?.userId) {
      formData.append("user_id", String(userInfo.userId));
    }
    if (userInfo?.phone) {
      formData.append("phone", userInfo.phone);
    }
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setResult(t('contactUsSuccess'));
        (event.target as HTMLFormElement).reset();
        setTimeout(() => {
          onClose();
          setResult("");
        }, 3000);
      } else {
        setResult(data.message || t('contactUsError'));
      }
    } catch (error) {
      setResult(t('contactUsNetworkError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`contact-us-drawer ${isOpen ? 'open' : ''}`}>
      <div className="contact-us-drawer__backdrop" onClick={onClose}></div>
      <div className="contact-us-drawer__paper">
        <Box className="contact-us-drawer__content" dir="rtl">
          <div className="contact-us-drawer__header">
            <Typography variant="h6">{t('contactUsTitle')}</Typography>
            <IconButton onClick={onClose}>
              <img src={Close} alt="Close" />
            </IconButton>
          </div>

        <form onSubmit={onSubmit} className="contact-us-drawer__form" noValidate>
          <FormControl fullWidth margin="normal" error={errors.type}>
            <InputLabel id="type-label">{t('contactUsType')} <span className="required-asterisk">*</span></InputLabel>
            <Select
              labelId="type-label"
              name="type"
              label={`${t('contactUsType')} *`}
              defaultValue="bug"
              onChange={() => setErrors(prev => ({ ...prev, type: false }))}
            >
              <MenuItem value="bug">{t('contactUsTypeBug')}</MenuItem>
              <MenuItem value="feature">{t('contactUsTypeFeature')}</MenuItem>
              <MenuItem value="other">{t('contactUsTypeOther')}</MenuItem>
            </Select>
          </FormControl>
          {errors.type && <div className="error-text">{t('contactUsEnterType')}</div>}

          <TextField
            fullWidth
            margin="normal"
            label={`${t('contactUsScreen')} *`}
            name="screen"
            value={screenUrl}
            onChange={(e) => setScreenUrl(e.target.value)}
            InputProps={{ readOnly: false }}
          />

          <TextField
            fullWidth
            margin="normal"
            label={`${t('contactUsDescription')} *`}
            name="message"
            multiline
            rows={5}
            error={errors.message}
            onChange={() => setErrors(prev => ({ ...prev, message: false }))}
          />
          {errors.message && <div className="error-text">{t('contactUsEnterMessage')}</div>}

          <Box mt={3}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth 
              disabled={isSubmitting}
              className="contact-us-drawer__submit-btn"
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('contactUsSend')}
            </Button>
          </Box>

          {result && (
            <Typography 
              variant="body2" 
              className={`contact-us-drawer__result ${result.includes('שגיאה') || result.includes('Error') ? 'error' : 'success'}`}
            >
              {result}
            </Typography>
          )}
        </form>
      </Box>
      </div>
    </div>
  );
}
